import { useEffect, useRef, useSyncExternalStore } from "react";

/** True when a key press is meant for a text field or an open dialog, not for a shortcut. */
function typing(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return true;
  return Boolean(document.querySelector(".dialog, .palette, .pop, .lightbox"));
}

/** A single-key shortcut such as Q or C, active while `enabled`. */
export function useShortcut(key: string, run: () => void, enabled = true) {
  const fn = useRef(run);
  fn.current = run;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e)) return;
      if (e.key.toLowerCase() !== key) return;
      e.preventDefault();
      fn.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [key, enabled]);
}

/* Small shared UI state: the mobile nav drawer and the command palette. */
let ui = { nav: false, palette: false };
const subs = new Set<() => void>();
export function setUi(p: Partial<typeof ui>) {
  ui = { ...ui, ...p };
  subs.forEach((s) => s());
}
export function useUi() {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => ui,
  );
}
